import { Component, ElementRef, HostListener, Input, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

import { AuthService } from 'src/app/shared/services/auth.service';
import { ADMINISTRATOR } from 'src/app/shared/services/user.service';
import { adminMenuSections } from '../../menu/admin-menu-items';
import { MenuItem } from '../../menu/menu-item';
import { MenuService } from '../../menu/menu.service';

/** Uma linha da busca: um destino do menu. */
export interface SearchEntry {
  label: string;
  /** Onde o item mora no menu ("IA", "Admin · Preços"…), mostrado ao lado. */
  group: string;
  icon: string;
  path?: string;
  externalUrl?: string;
  admin: boolean;
  testId: string;
}

/** Quantas linhas a lista mostra no máximo (o resto aparece refinando o texto). */
export const SEARCH_LIMIT = 12;

/**
 * Busca global no topo (07/10/2026, pedido do Bruno): antes vivia dentro do grupo
 * Admin da barra lateral e só para administrador. Agora fica no header, para TODOS:
 *
 *  - os itens do cliente saem do `MenuService.getMenuItems()` — os mesmos filtros de
 *    perfil, realm, simulação e IA da barra lateral, então a busca nunca mostra uma
 *    tela que o menu do usuário não mostra;
 *  - os itens do Admin (`adminMenuSections`) entram só para administrador, pela mesma
 *    regra que mostra o grupo Admin na barra (`profile_id === ADMINISTRATOR`).
 *
 * O índice é montado a cada abertura: trocar de realm ou entrar em simulação muda o
 * menu, e a busca acompanha sem precisar assinar nada.
 *
 * Ctrl+K (⌘K no Mac) abre de qualquer tela; ↑/↓ navegam, Enter abre, Esc fecha.
 */
@Component({
  selector: 'app-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss'],
})
export class GlobalSearchComponent {
  /** Header de celular: só a lupa, sem o campo falso. */
  @Input() compact = false;

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  isOpen = false;
  query = '';
  results: SearchEntry[] = [];
  activeIndex = 0;

  private entries: SearchEntry[] = [];

  constructor(
    private authService: AuthService,
    private menuService: MenuService,
    private router: Router,
  ) {}

  get isMac(): boolean {
    return /Mac|iPhone|iPad/.test(navigator.platform || '');
  }

  get shortcutLabel(): string {
    return this.isMac ? '⌘ K' : 'Ctrl K';
  }

  @HostListener('document:keydown', ['$event'])
  onShortcut(event: KeyboardEvent): void {
    if (!(event.ctrlKey || event.metaKey) || event.key?.toLowerCase() !== 'k') {
      return;
    }
    event.preventDefault();
    this.isOpen ? this.focusInput() : this.open();
  }

  open(): void {
    this.entries = this.buildEntries();
    this.isOpen = true;
    this.search('');
    // O campo só existe depois do próximo ciclo (*ngIf).
    setTimeout(() => this.focusInput());
  }

  close(): void {
    this.isOpen = false;
    this.query = '';
  }

  search(query: string): void {
    this.query = query;
    this.activeIndex = 0;

    const term = normalize(query);

    if (!term) {
      // Sem texto: os destinos do próprio usuário primeiro (o menu dele), na ordem do menu.
      this.results = this.entries.filter(entry => !entry.admin).slice(0, SEARCH_LIMIT);
      return;
    }

    this.results = this.entries
      .map(entry => ({ entry, score: score(entry, term) }))
      .filter(match => match.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, SEARCH_LIMIT)
      .map(match => match.entry);
  }

  onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.activeIndex = this.results.length ? (this.activeIndex + 1) % this.results.length : 0;
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.activeIndex = this.results.length ? (this.activeIndex - 1 + this.results.length) % this.results.length : 0;
        break;
      case 'Enter':
        event.preventDefault();
        if (this.results[this.activeIndex]) {
          this.go(this.results[this.activeIndex]);
        }
        break;
      case 'Escape':
        // Para aqui: o mesmo Esc fecharia a gaveta do menu no celular.
        event.stopPropagation();
        this.close();
        break;
    }
  }

  go(entry: SearchEntry): void {
    this.close();

    if (entry.externalUrl) {
      window.open(entry.externalUrl, '_blank', 'noopener');
      return;
    }

    this.router.navigateByUrl(entry.path);
  }

  trackByTestId(_index: number, entry: SearchEntry): string {
    return entry.testId;
  }

  private focusInput(): void {
    this.searchInput?.nativeElement.focus();
    this.searchInput?.nativeElement.select();
  }

  private buildEntries(): SearchEntry[] {
    const entries: SearchEntry[] = [];

    this.menuService.getMenuItems().forEach((item: MenuItem) => {
      if (item.children?.length) {
        item.children.forEach(child => entries.push(fromMenuItem(child, item.text)));
        return;
      }
      entries.push(fromMenuItem(item, 'Menu'));
    });

    const user = this.authService.getUser();

    if (user?.profile_id === ADMINISTRATOR) {
      adminMenuSections.forEach(section => section.items.forEach(item => entries.push({
        label: item.label,
        group: `Admin · ${section.label}`,
        icon: item.icon,
        path: item.path,
        externalUrl: item.externalPath ? `${environment.baseUrl}${item.externalPath}` : undefined,
        admin: true,
        testId: item.testId,
      })));
    }

    return entries;
  }
}

function fromMenuItem(item: MenuItem, group: string): SearchEntry {
  return {
    label: item.text,
    group,
    icon: item.icon || 'fa-file',
    path: item.path,
    admin: false,
    testId: item.dataTestid || `menu${item.path.replace(/\//g, '-')}`,
  };
}

/** Caixa e acento não contam: "precos" acha "Preços". */
export function normalize(text: string): string {
  return (text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

/**
 * Relevância: rótulo que começa com o termo > rótulo que contém > palavra do rótulo que
 * começa com ele > grupo que contém (traz a seção inteira, como a busca antiga do Admin).
 */
function score(entry: SearchEntry, term: string): number {
  const label = normalize(entry.label);
  const group = normalize(entry.group);

  if (label.startsWith(term)) {
    return 4;
  }
  if (label.includes(term)) {
    return label.split(/\s+/).some(word => word.startsWith(term)) ? 3 : 2;
  }
  if (group.includes(term)) {
    return 1;
  }
  return 0;
}
